import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Space, InputNumber, message, Popconfirm } from 'antd';
import { channelApi, agentServiceApi, agentApi, type ChannelDTO, type AgentServiceChannelDTO, type AgentDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

const ChannelManage: React.FC = () => {
  const [list, setList] = useState<ChannelDTO[]>([]);
  const [agentServices, setAgentServices] = useState<AgentServiceChannelDTO[]>([]);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    channelApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载渠道列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    agentApi.list().then((res) => setAgents(res.data)).catch(() => setAgents([]));
    agentServiceApi.list().then((res) => setAgentServices(res.data)).catch(() => setAgentServices([]));
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ agentServiceId: undefined });
    setModalOpen(true);
  };

  const openEdit = (c: ChannelDTO) => {
    setEditingId(c.id);
    form.setFieldsValue({
      agentId: c.agentId,
      channelCode: c.channelCode,
      channelName: c.channelName,
      volumeCoefficient: c.volumeCoefficient,
    });
    setModalOpen(true);
  };

  const onAgentServiceSelect = (val: string) => {
    if (!val) return;
    const parts = val.split('::');
    const agentId = parts[0];
    const serviceId = parts[1];
    const svc = agentServices.find(
      (s) => s.agentId === agentId && String(s.serviceId) === serviceId
    );
    if (svc) {
      form.setFieldsValue({
        agentId: svc.agentId,
        channelCode: String(svc.serviceId),
        channelName: svc.serviceName ?? '',
      });
    }
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload: Partial<ChannelDTO> = {
      agentId: values.agentId as string,
      channelCode: values.channelCode as string,
      channelName: values.channelName as string | undefined,
      volumeCoefficient: values.volumeCoefficient as number | undefined,
    };
    if (editingId) {
      channelApi
        .update(editingId, payload)
        .then(() => {
          message.success('更新成功');
          setModalOpen(false);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'));
    } else {
      channelApi
        .save(payload)
        .then(() => {
          message.success('创建成功');
          setModalOpen(false);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '创建失败'));
    }
  };

  const onDelete = (id: string) => {
    channelApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const agentServiceOptions = agentServices.map((s) => ({
    value: `${s.agentId}::${s.serviceId}`,
    label: `${agents.find((a) => a.id === s.agentId)?.basicInfo?.name ?? s.agentId} - ${s.serviceName ?? s.serviceId} (${s.stype ?? '-'})`,
  }));

  return (
    <>
      <Card title="渠道列表" extra={<Button type="primary" onClick={openAdd}>新增渠道</Button>}>
        <p style={{ marginBottom: 16 }}>
          新增渠道时，请从「代理渠道列表」中选择一个代理渠道，系统将带出渠道信息，可修改后保存。线路列表中绑定渠道时从本列表选择。
        </p>
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '渠道编码', dataIndex: 'channelCode', width: 120 },
            { title: '渠道名称', dataIndex: 'channelName', width: 160, ellipsis: true },
            { title: '体积系数', dataIndex: 'volumeCoefficient', width: 100 },
            { title: '代理ID', dataIndex: 'agentId', width: 120, ellipsis: true },
            {
              title: '操作',
              width: 140,
              render: (_, c: ChannelDTO) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(c)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定删除该渠道？" onConfirm={() => onDelete(c.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑渠道' : '新增渠道（从代理渠道选择）'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {!editingId && (
            <Form.Item
              name="agentServiceId"
              label="选择代理渠道"
              rules={[{ required: true, message: '请从代理渠道列表中选择一个' }]}
            >
              <Select
                placeholder="请选择代理渠道"
                options={agentServiceOptions}
                onChange={onAgentServiceSelect}
                showSearch
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}
          {!editingId && (
            <Form.Item name="agentId" hidden rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {editingId && (
            <Form.Item name="agentId" label="代理ID" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="channelCode" label="渠道编号" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="channelName" label="渠道名称">
            <Input />
          </Form.Item>
          <Form.Item name="volumeCoefficient" label="体积系数">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="如 5000" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ChannelManage;
